/**
 * Search suggestions module for Oxford term dates
 */

import { getAvailableYears, getCurrentAcademicYear } from '../data/termService.js';
import { TERM_NAMES, DAY_NAMES, MONTH_NAMES } from './parser/patterns.js';

/**
 * Generate search suggestions based on partial input
 * @param {string} input - Partial user input
 * @param {Object} options - Options for suggestion generation
 * @returns {Array<Object>} Array of suggestion objects
 */
export function generateSuggestions(input, options = {}) {
  if (!input) {
    const defaults = getDefaultSuggestions();
    return defaults.slice(0, options.maxSuggestions || 8);
  }
  
  // Special case for single 'w' - it's meaningful for week suggestions
  if (input.length < 2 && input.toLowerCase() !== 'w') {
    const defaults = getDefaultSuggestions();
    return defaults.slice(0, options.maxSuggestions || 8);
  }
  
  const normalized = input.toLowerCase().trim();
  const suggestions = [];
  
  // Add term-based suggestions
  suggestions.push(...getTermSuggestions(normalized, options));
  
  // Add week-based suggestions
  suggestions.push(...getWeekSuggestions(normalized, options));
  
  // Add day-based suggestions
  suggestions.push(...getDaySuggestions(normalized, options));
  
  // Add date format suggestions
  suggestions.push(...getDateFormatSuggestions(normalized));
  
  // Remove duplicates and limit
  const unique = Array.from(new Map(
    suggestions.map(s => [s.text, s])
  ).values());
  
  return unique.slice(0, options.maxSuggestions || 8);
}

/**
 * Get default suggestions when no input
 * @returns {Array<Object>} Default suggestions
 */
function getDefaultSuggestions() {
  const currentYear = getCurrentAcademicYear();
  const nextYear = getNextAcademicYear(currentYear);
  
  return [
    {
      text: `Week 1 Michaelmas ${currentYear}`,
      type: 'example',
      description: 'First week of Michaelmas term'
    },
    {
      text: `Week 5 Hilary ${currentYear}`,
      type: 'example',
      description: 'Fifth week of Hilary term'
    },
    {
      text: `Trinity ${currentYear} Week 8`,
      type: 'example',
      description: 'Last week of Trinity full term'
    },
    {
      text: 'Tuesday Week 3',
      type: 'example',
      description: 'Specific day in a term week'
    }
  ];
}

/**
 * Get term-based suggestions
 * @param {string} input - Normalized input
 * @param {Object} options - Options
 * @returns {Array<Object>} Term suggestions
 */
function getTermSuggestions(input, options = {}) {
  const suggestions = [];
  const terms = [
    { name: 'Michaelmas', abbrev: ['mich', 'mt'] },
    { name: 'Hilary', abbrev: ['hil', 'ht'] },
    { name: 'Trinity', abbrev: ['trin', 'tt'] }
  ];
  
  const currentYear = getCurrentAcademicYear();
  
  // Check for year-specific term aliases (e.g., MT25, HT26)
  const yearTermMatch = input.match(/^(mt|ht|tt)(\d{2})?$/i);
  if (yearTermMatch) {
    const [, termAlias, yearPart] = yearTermMatch;
    const termMap = {
      'mt': 'Michaelmas',
      'ht': 'Hilary',
      'tt': 'Trinity'
    };
    const fullTerm = termMap[termAlias.toLowerCase()];
    
    if (yearPart) {
      // Specific year provided
      const fullYear = `20${yearPart}`;
      const academicYear = getAcademicYearFromCalendarYear(fullTerm, fullYear);
      suggestions.push({
        text: `${fullTerm} ${academicYear}`,
        type: 'term',
        description: `${fullTerm} term of ${academicYear}`
      });
      suggestions.push({
        text: `Week 1 ${fullTerm} ${academicYear}`,
        type: 'term-week',
        description: `First week of ${fullTerm} term`
      });
    } else {
      // Just term alias without year
      suggestions.push({
        text: `${fullTerm} ${currentYear}`,
        type: 'term',
        description: `${fullTerm} term of ${currentYear}`
      });
      suggestions.push({
        text: `Week 1 ${fullTerm} ${currentYear}`,
        type: 'term-week',
        description: `First week of ${fullTerm} term`
      });
    }
    return suggestions;
  }
  
  for (const term of terms) {
    const termLower = term.name.toLowerCase();
    
    // Check if input matches term name or abbreviation
    const matches = termLower.startsWith(input) || 
                   term.abbrev.some(a => a.startsWith(input));
    
    if (matches) {
      // Add term with current year
      suggestions.push({
        text: `${term.name} ${currentYear}`,
        type: 'term',
        description: `${term.name} term of ${currentYear}`
      });
      
      // Add term with week examples
      suggestions.push({
        text: `Week 1 ${term.name} ${currentYear}`,
        type: 'term-week',
        description: `First week of ${term.name} term`
      });
      
      if (input.length > 2) {
        suggestions.push({
          text: `Week 5 ${term.name} ${currentYear}`,
          type: 'term-week',
          description: `Fifth week of ${term.name} term`
        });
      }
    }
  }
  
  return suggestions;
}

/**
 * Get week-based suggestions
 * @param {string} input - Normalized input
 * @param {Object} options - Options
 * @returns {Array<Object>} Week suggestions
 */
function getWeekSuggestions(input, options = {}) {
  const suggestions = [];
  
  // Check for week aliases (w0, w1, w2, etc.)
  const weekAliasMatch = input.match(/^w(\d{1,2})$/i);
  if (weekAliasMatch) {
    const weekNum = parseInt(weekAliasMatch[1]);
    if (weekNum >= 0 && weekNum <= 12) {
      const currentYear = getCurrentAcademicYear();
      
      suggestions.push({
        text: `Week ${weekNum} Michaelmas ${currentYear}`,
        type: 'week',
        description: `Week ${weekNum} of Michaelmas term`
      });
      
      suggestions.push({
        text: `Week ${weekNum} Hilary ${currentYear}`,
        type: 'week',
        description: `Week ${weekNum} of Hilary term`
      });
      
      suggestions.push({
        text: `Week ${weekNum} Trinity ${currentYear}`,
        type: 'week',
        description: `Week ${weekNum} of Trinity term`
      });
    }
    return suggestions;
  }
  
  if (input.startsWith('w') || input.startsWith('week')) {
    const currentYear = getCurrentAcademicYear();
    
    // Extract week number if present
    const weekMatch = input.match(/w(?:ee)?k?\s*(\d+)/);
    const weekNum = weekMatch ? parseInt(weekMatch[1]) : null;
    
    if (weekNum !== null && weekNum >= 0 && weekNum <= 12) {
      // Specific week number with all terms
      suggestions.push({
        text: `Week ${weekNum} Michaelmas ${currentYear}`,
        type: 'week',
        description: `Week ${weekNum} of Michaelmas term`
      });
      
      suggestions.push({
        text: `Week ${weekNum} Hilary ${currentYear}`,
        type: 'week',
        description: `Week ${weekNum} of Hilary term`
      });
      
      suggestions.push({
        text: `Week ${weekNum} Trinity ${currentYear}`,
        type: 'week',
        description: `Week ${weekNum} of Trinity term`
      });
    } else {
      // Generic week suggestions
      const weeks = [0, 1, 4, 5, 8];
      for (const week of weeks) {
        suggestions.push({
          text: `Week ${week}`,
          type: 'week-partial',
          description: week === 0 ? 'Week before full term' : 
                      week === 8 ? 'Last week of full term' : 
                      `Week ${week} of term`
        });
      }
    }
  }
  
  return suggestions;
}

/**
 * Get day-based suggestions
 * @param {string} input - Normalized input
 * @param {Object} options - Options
 * @returns {Array<Object>} Day suggestions
 */
function getDaySuggestions(input, options = {}) {
  const suggestions = [];
  const days = [
    { full: 'Monday', abbrev: ['mon'] },
    { full: 'Tuesday', abbrev: ['tue'] },
    { full: 'Wednesday', abbrev: ['wed'] },
    { full: 'Thursday', abbrev: ['thu'] },
    { full: 'Friday', abbrev: ['fri'] },
    { full: 'Saturday', abbrev: ['sat'] },
    { full: 'Sunday', abbrev: ['sun'] }
  ];
  
  const currentYear = getCurrentAcademicYear();
  
  for (const day of days) {
    // Check both full name and abbreviations
    const matchesFull = day.full.toLowerCase().startsWith(input);
    const matchesAbbrev = day.abbrev.some(a => a.startsWith(input));
    
    if (matchesFull || matchesAbbrev) {
      suggestions.push({
        text: `${day.full} Week 1`,
        type: 'day-partial',
        description: `${day.full} of Week 1`
      });
      
      if (input.length > 2) {
        suggestions.push({
          text: `${day.full} Week 5 Michaelmas ${currentYear}`,
          type: 'day-week',
          description: `${day.full} of Week 5, Michaelmas term`
        });
      }
    }
  }
  
  return suggestions;
}

/**
 * Get date format suggestions
 * @param {string} input - Normalized input
 * @param {Object} options - Options
 * @returns {Array<Object>} Date format suggestions
 */
function getDateFormatSuggestions(input) {
  const suggestions = [];
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Check if input looks like a date
  const hasNumber = /\d/.test(input);
  
  if (hasNumber) {
    const currentYear = new Date().getFullYear();
    
    // Check for day number
    const dayMatch = input.match(/^(\d{1,2})/);
    if (dayMatch) {
      const day = parseInt(dayMatch[1]);
      if (day >= 1 && day <= 31) {
        // Suggest months for this day
        const relevantMonths = getRelevantMonths();
        for (const month of relevantMonths) {
          suggestions.push({
            text: `${day} ${month} ${currentYear}`,
            type: 'date',
            description: `Date in ${month}`
          });
        }
      }
    }
  }
  
  // Month name suggestions
  for (const month of months) {
    if (month.toLowerCase().startsWith(input)) {
      const currentYear = new Date().getFullYear();
      suggestions.push({
        text: `15 ${month} ${currentYear}`,
        type: 'date',
        description: `Mid-${month} date`
      });
    }
  }
  
  return suggestions;
}

/**
 * Get relevant months for current time of year
 * @returns {Array<string>} Array of month names
 */
function getRelevantMonths() {
  const currentMonth = new Date().getMonth();
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Return current month and next 3 months
  const relevant = [];
  for (let i = 0; i < 4; i++) {
    relevant.push(months[(currentMonth + i) % 12]);
  }
  return relevant;
}

/**
 * Get next academic year from current
 * @param {string} currentYear - Current academic year (e.g., "2024-25")
 * @returns {string} Next academic year
 */
function getNextAcademicYear(currentYear) {
  if (!currentYear) return '2025-26';
  
  const [startYear] = currentYear.split('-');
  const nextStart = parseInt(startYear) + 1;
  const nextEnd = (nextStart + 1).toString().slice(-2);
  return `${nextStart}-${nextEnd}`;
}

/**
 * Get academic year from calendar year and term
 * @param {string} term - Term name (Michaelmas, Hilary, Trinity)
 * @param {string} calendarYear - Calendar year (e.g., "2025")
 * @returns {string} Academic year (e.g., "2024-25")
 */
function getAcademicYearFromCalendarYear(term, calendarYear) {
  const year = parseInt(calendarYear);
  if (term.toLowerCase() === 'michaelmas') {
    // Michaelmas starts the academic year
    return `${year}-${(year + 1).toString().slice(-2)}`;
  } else {
    // Hilary and Trinity are in the second part of academic year
    return `${year - 1}-${year.toString().slice(-2)}`;
  }
}

/**
 * Format suggestions for display
 * @param {Array<Object>} suggestions - Raw suggestions
 * @returns {Array<string>} Formatted suggestion strings
 */
export function formatSuggestionsForDisplay(suggestions) {
  return suggestions.map(s => s.text);
}

/**
 * Get suggestion details
 * @param {Object} suggestion - Suggestion object
 * @returns {string} HTML string with suggestion details
 */
export function getSuggestionHTML(suggestion) {
  return `
    <div class="suggestion-item" data-type="${suggestion.type}">
      <span class="suggestion-text">${suggestion.text}</span>
      <span class="suggestion-description">${suggestion.description}</span>
    </div>
  `;
}